import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import PopupOverlay from '../../organisms/Background/PopupOverlay';
import Button from '../../atoms/Button';
import './index.scss';

/**
 * Generic destructive-action confirmation dialog. Used by venue/event delete
 * flows; pass any text via translation keys or raw strings.
 */
export default function ConfirmDeleteDialog({
    isOpen,
    title,
    description,
    confirmLabel,
    cancelLabel,
    isSubmitting = false,
    errorMessage = null,
    onConfirm,
    onCancel,
    targetName = null,
}) {
    const { t } = useTranslation();

    useEffect(() => {
        if (!isOpen) return undefined;
        const handler = (event) => {
            if (event.key !== 'Escape') {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation?.();
            if (!isSubmitting) {
                onCancel?.();
            }
        };
        document.addEventListener('keydown', handler, true);
        return () => document.removeEventListener('keydown', handler, true);
    }, [isOpen, isSubmitting, onCancel]);

    if (!isOpen) {
        return null;
    }

    const resolvedConfirmLabel = isSubmitting
        ? t('common.deleting', 'Deleting…')
        : (confirmLabel || t('common.delete', 'Delete'));
    const resolvedCancelLabel = cancelLabel || t('common.cancel', 'Cancel');

    // Render into the global #portal so the fixed overlay escapes the page's
    // stacking context (#content children sit below the fixed mobile header,
    // z-index 50) and paints above it. When nested inside an already-portaled
    // modal this still lands in #portal, and its higher z-index keeps it above
    // the parent dialog.
    const portalTarget = typeof document !== 'undefined'
        ? document.getElementById('portal')
        : null;

    const overlay = (
        <PopupOverlay
            onClose={isSubmitting ? undefined : onCancel}
            closeOnOverlayClick={!isSubmitting}
            closeOnEsc={false}
            zIndex={7100}
        >
            <section
                className="confirm-delete-dialog"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="confirm-delete-dialog-title"
                aria-describedby="confirm-delete-dialog-description"
            >
                <h3 id="confirm-delete-dialog-title" className="confirm-delete-dialog__title">
                    {title}
                </h3>
                <p id="confirm-delete-dialog-description" className="confirm-delete-dialog__description">
                    {description}
                </p>
                {targetName ? (
                    <p className="confirm-delete-dialog__target">{targetName}</p>
                ) : null}
                {errorMessage ? (
                    <p className="confirm-delete-dialog__error" role="alert">
                        {errorMessage}
                    </p>
                ) : null}
                <div className="confirm-delete-dialog__actions">
                    <Button
                        type="button"
                        variant="secondary"
                        shape="pill"
                        size="medium"
                        onClick={onCancel}
                        disabled={isSubmitting}
                        className="confirm-delete-dialog__cancel"
                    >
                        {resolvedCancelLabel}
                    </Button>
                    <button
                        type="button"
                        className="confirm-delete-dialog__confirm"
                        onClick={onConfirm}
                        disabled={isSubmitting}
                    >
                        {resolvedConfirmLabel}
                    </button>
                </div>
            </section>
        </PopupOverlay>
    );

    return portalTarget ? createPortal(overlay, portalTarget) : overlay;
}

ConfirmDeleteDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    title: PropTypes.node.isRequired,
    description: PropTypes.node.isRequired,
    confirmLabel: PropTypes.node,
    cancelLabel: PropTypes.node,
    isSubmitting: PropTypes.bool,
    errorMessage: PropTypes.node,
    onConfirm: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    targetName: PropTypes.node,
};
